import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const TOKEN_KEY = "access_token";

// Attach the Bearer token (from localStorage) to every request.
// This makes auth work even when browsers block third-party cookies
// (e.g. inside preview iframes or on tablets).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercept 401 responses to redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/")) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ==================== BRANCHES ====================
export const getBranches = async () => {
  const response = await api.get("/branches");
  return response.data;
};

// ==================== STAFF ====================
export const getStaff = async () => {
  const response = await api.get("/staff");
  return response.data;
};

export const getStaffById = async (staffId) => {
  const response = await api.get(`/staff/${staffId}`);
  return response.data;
};

export const createStaff = async (data) => {
  const response = await api.post("/staff", data);
  return response.data;
};

export const updateStaff = async (staffId, data) => {
  const response = await api.put(`/staff/${staffId}`, data);
  return response.data;
};

export const deleteStaff = async (staffId) => {
  const response = await api.delete(`/staff/${staffId}`);
  return response.data;
};

// ==================== SERVICES ====================
export const getServices = async () => {
  const response = await api.get("/services");
  return response.data;
};

export const getServiceById = async (serviceId) => {
  const response = await api.get(`/services/${serviceId}`);
  return response.data;
};

export const createService = async (data) => {
  const response = await api.post("/services", data);
  return response.data;
};

export const updateService = async (serviceId, data) => {
  const response = await api.put(`/services/${serviceId}`, data);
  return response.data;
};

export const deleteService = async (serviceId) => {
  const response = await api.delete(`/services/${serviceId}`);
  return response.data;
};

export const matchService = async (name, duration, paymentMethod) => {
  const response = await api.get("/services/match", {
    params: { name, duration, payment_method: paymentMethod },
  });
  return response.data;
};

// ==================== TRANSACTIONS ====================
export const getTransactions = async (params = {}) => {
  const response = await api.get("/transactions", { params });
  return response.data;
};

// ==================== CUSTOMERS (CRM) ====================
export const searchCustomers = async (search) =>
  (await api.get("/customers", { params: { search, limit: 8 } })).data;

export const getCustomers = async (search) =>
  (await api.get("/customers", { params: { search: search || undefined, limit: 500 } })).data;
export const createCustomer = async (data) => (await api.post("/customers", data)).data;
export const updateCustomer = async (id, data) => (await api.put(`/customers/${id}`, data)).data;
export const getCustomerHistory = async (id) => (await api.get(`/customers/${id}/history`)).data;

export const getAnalyticsDashboard = async (params = {}) =>
  (await api.get("/analytics/dashboard", { params })).data;

export const getTransactionById = async (transactionId) => {
  const response = await api.get(`/transactions/${transactionId}`);
  return response.data;
};

export const createTransaction = async (data) => {
  const response = await api.post("/transactions", data);
  return response.data;
};

export const updateTransaction = async (transactionId, data) => {
  const response = await api.put(`/transactions/${transactionId}`, data);
  return response.data;
};

export const deleteTransaction = async (transactionId) => {
  const response = await api.delete(`/transactions/${transactionId}`);
  return response.data;
};

// ==================== EXPENSES ====================
export const getExpenses = async (params = {}) => {
  const response = await api.get("/expenses", { params });
  return response.data;
};

export const createExpense = async (data) => {
  const response = await api.post("/expenses", data);
  return response.data;
};

export const deleteExpense = async (expenseId) => {
  const response = await api.delete(`/expenses/${expenseId}`);
  return response.data;
};

// ==================== CASH FLOW ====================
export const getDailyCashflow = async (date, branchId) => {
  const response = await api.get(`/cashflow/${date}/${branchId}`);
  return response.data;
};

export const saveDailyCashflow = async (data) => {
  const response = await api.post("/cashflow", data);
  return response.data;
};

// ==================== PAYROLL ====================
export const getPayrollSummary = async (staffId, startDate, endDate) => {
  const response = await api.get(`/payroll/summary/${staffId}`, {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

export const getPayrollSummaryAll = async (startDate, endDate) => {
  const response = await api.get("/payroll/summary-all", {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

export const recordPayment = async (data) => {
  const response = await api.post("/payroll/payment", data);
  return response.data;
};

export const payAll = async (startDate, endDate) => {
  const response = await api.post("/payroll/pay-all", { period_start: startDate, period_end: endDate });
  return response.data;
};

export const getPayrollLedger = async (startDate, endDate) =>
  (await api.get("/payroll/ledger", { params: { start_date: startDate, end_date: endDate } })).data;

export const getPayments = async (params = {}) => {
  const response = await api.get("/payroll/payments", { params });
  return response.data;
};

export const deletePayment = async (paymentId) => {
  const response = await api.delete(`/payroll/payments/${paymentId}`);
  return response.data;
};

// ==================== ROSTER ====================
export const getRoster = async (startDate, endDate) => {
  const response = await api.get("/roster", {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

export const createRosterAssignment = async (data) => {
  const response = await api.post("/roster", data);
  return response.data;
};

export const deleteRosterAssignment = async (assignmentId) => {
  const response = await api.delete(`/roster/${assignmentId}`);
  return response.data;
};

export const deleteRosterByDetails = async (date, branchId, staffId) => {
  const response = await api.delete("/roster/by-details", {
    params: { date, branch_id: branchId, staff_id: staffId }
  });
  return response.data;
};

// ==================== LEAVE REQUESTS ====================
export const getLeaveRequests = async (params = {}) => {
  const response = await api.get("/leave-requests", { params });
  return response.data;
};

export const createLeaveRequest = async (data) => {
  const response = await api.post("/leave-requests", data);
  return response.data;
};

export const updateLeaveRequest = async (requestId, data) => {
  const response = await api.put(`/leave-requests/${requestId}`, data);
  return response.data;
};

export const deleteLeaveRequest = async (requestId) => {
  const response = await api.delete(`/leave-requests/${requestId}`);
  return response.data;
};

export const getRosterRemovals = async (startDate, endDate) => {
  const response = await api.get("/roster/removals", {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

// ==================== ANALYTICS ====================
export const getStaffPerformance = async (params = {}) => {
  const response = await api.get("/analytics/staff-performance", { params });
  return response.data;
};

export const getDailySummary = async (date, branchId = null) => {
  const params = { date };
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/analytics/daily-summary", { params });
  return response.data;
};

export const getWeeklySummary = async (startDate, endDate, branchId = null) => {
  const params = { start_date: startDate, end_date: endDate };
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/analytics/weekly-summary", { params });
  return response.data;
};

export const getMonthlySummary = async (year, branchId = null) => {
  const params = { year };
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/analytics/monthly-summary", { params });
  return response.data;
};

export const getBusyDays = async (params = {}) => {
  const response = await api.get("/analytics/busy-days", { params });
  return response.data;
};

export const getPeakHours = async (params = {}) => {
  const response = await api.get("/analytics/peak-hours", { params });
  return response.data;
};

// ==================== EXPORT ====================
export const exportTransactions = async (params = {}) => {
  const response = await api.get("/export/transactions", {
    params,
    responseType: "blob",
  });
  return response;
};

export const exportStaffPerformance = async (params = {}) => {
  const response = await api.get("/export/staff-performance", {
    params,
    responseType: "blob",
  });
  return response;
};

// ==================== EFFECTIVE ROSTER ====================
export const getEffectiveRoster = async (date, branchId = null) => {
  const params = { date };
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/roster/effective", { params });
  return response.data;
};

// ==================== CASH FLOW REPORTS ====================
export const getCashflowReport = async (startDate, endDate, branchId = null) => {
  const params = { start_date: startDate, end_date: endDate };
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/reports/cashflow", { params });
  return response.data;
};

export const saveCashflowNote = async (date, branchId, notes) => {
  const response = await api.post("/cashflow/note", { date, branch_id: branchId, notes });
  return response.data;
};

// ==================== SAFE MANAGEMENT ====================
export const getSafeBalance = async (branchId = null) => {
  const params = {};
  if (branchId) params.branch_id = branchId;
  const response = await api.get("/safe/balance", { params });
  return response.data;
};

export const getSafeHistory = async (params = {}) => {
  const response = await api.get("/safe/history", { params });
  return response.data;
};

export const createSafeWithdrawal = async (data) => {
  const response = await api.post("/safe/withdraw", data);
  return response.data;
};

export const deleteSafeWithdrawal = async (id) => {
  const response = await api.delete(`/safe/withdraw/${id}`);
  return response.data;
};

// ==================== ATTENDANCE (CLOCK IN/OUT) ====================
export const getAttendance = async (date, branchId) =>
  (await api.get("/attendance", { params: { date, branch_id: branchId } })).data;
export const clockIn = async (data) => (await api.post("/attendance/clock-in", data)).data;
export const clockOut = async (data) => (await api.post("/attendance/clock-out", data)).data;
export const setAttendance = async (data) => (await api.post("/attendance/set", data)).data;

// ==================== SHOP FINANCIAL CONFIG ====================
export const getShopConfig = async () => (await api.get("/admin/shop-config")).data;
export const updateShopConfig = async (data) => (await api.put("/admin/shop-config", data)).data;

// ==================== DAILY EARNINGS / CASH PAYOUTS ====================
export const getDailyEarnings = async (date, branchId, cashOnly = false) =>
  (await api.get("/payroll/daily", { params: { date, branch_id: branchId, cash_only: cashOnly } })).data;
export const confirmCashPaid = async (data) => (await api.post("/payroll/confirm-cash-paid", data)).data;
export const dailySettle = async (data) => (await api.post("/payroll/daily-settle", data)).data;
export const getDailyPayouts = async (params = {}) => (await api.get("/payroll/daily-payouts", { params })).data;
export const deleteDailyPayout = async (id) => (await api.delete(`/payroll/daily-payouts/${id}`)).data;

// ==================== OTHER INCOME (RETAIL) ====================
export const getOtherIncome = async (params = {}) => (await api.get("/other-income", { params })).data;
export const createOtherIncome = async (data) => (await api.post("/other-income", data)).data;
export const deleteOtherIncome = async (id) => (await api.delete(`/other-income/${id}`)).data;

// ==================== SAFE DROPS (drawer -> safe) ====================
export const getSafeDrops = async (params = {}) => (await api.get("/safe-drops", { params })).data;
export const createSafeDrop = async (data) => (await api.post("/safe-drops", data)).data;
export const deleteSafeDrop = async (id) => (await api.delete(`/safe-drops/${id}`)).data;

// ==================== ACCOUNTING DAILY ====================
export const getAccountingDaily = async (date, branchId) =>
  (await api.get("/accounting/daily", { params: { date, branch_id: branchId } })).data;
export const setOpeningCash = async (data) => (await api.post("/accounting/opening-cash", data)).data;
export const setActualCash = async (data) => (await api.post("/accounting/actual-cash", data)).data;

// ==================== APP CONFIG / DEMO ====================
export const getConfig = async () => {
  const response = await api.get("/config");
  return response.data;
};

export const resetDemoData = async () => {
  const response = await api.post("/admin/reset-demo");
  return response.data;
};

export default api;
